import { UnionLogo } from '@/components/icons'
import { ROUTE_LOGIN } from '@/constants/Routes'
import { startSignup } from '@/services/auth'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

/**
 * Email-first signup entry. Ask for an email, email a signup link, and say "check your inbox" -- the
 * server answers identically whether or not the address already has an account, so this page never
 * reveals which. The full profile form lives at /signup/:token, reached by opening that link.
 *
 * A few links can be sent in a row; after that the server answers 429 with how long to wait
 * (Retry-After), and the send buttons count down until then.
 *
 * Account claiming is retired (the server refuses it): there is one way to an account now, and this
 * is its front door.
 */
export const SignUpStart = () => {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>()
  // When the next link may be asked for (epoch ms), and the seconds left until then.
  const [waitUntil, setWaitUntil] = useState<number | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(0)

  useEffect(() => {
    if (!waitUntil) return
    const tick = () => {
      const left = Math.max(0, Math.ceil((waitUntil - Date.now()) / 1000))
      setSecondsLeft(left)
      if (left === 0) setWaitUntil(null)
    }
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [waitUntil])

  const waiting = secondsLeft > 0

  const send = async (address: string) => {
    setSubmitting(true)
    setError(undefined)
    try {
      // Deliberately ignore the outcome: registered or not, the answer is the same.
      await startSignup(address)
      setSentTo(address)
    } catch (err: any) {
      const retryAfter = Number(err?.response?.headers?.['retry-after'])
      if (err?.response?.status === 429 && retryAfter > 0) {
        setWaitUntil(Date.now() + retryAfter * 1000)
      }
      setError(err?.response?.data?.message || t('auth.signup.startFailed', 'Something went wrong. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed || waiting) return
    await send(trimmed)
  }

  const waitLabel = t('auth.signup.sendAgainIn', 'Send again in {{seconds}}s', { seconds: secondsLeft })

  if (sentTo) {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <div className="w-[300px] xs:w-[345px] flex flex-col gap-y-8 text-center">
          <div className="flex flex-col justify-center items-center gap-y-12">
            <UnionLogo className="text-white" />
            <p className="text-xl font-bold text-white">{t('auth.signup.checkInboxTitle', 'Check your inbox')}</p>
          </div>
          <p className="text-white text-sm leading-relaxed">
            {t(
              'auth.signup.checkInboxBody',
              'If {{email}} can be used, we sent it a link to finish creating your account. The link expires shortly.',
              { email: sentTo }
            )}
          </p>
          {error && <p className="text-xs text-warn">{error}</p>}
          <div className="flex flex-col items-center gap-y-2">
            <button
              type="button"
              disabled={submitting || waiting}
              onClick={() => send(sentTo)}
              className={`${
                submitting || waiting ? 'bg-gray-400 text-gray-200' : 'bg-azul text-white hover:bg-primary'
              } w-60 rounded-full py-3 px-5`}
            >
              {waiting ? waitLabel : t('auth.signup.sendAgain', 'Send the link again')}
            </button>
            <Link to={ROUTE_LOGIN} className="text-white underline hover:text-opacity-70 text-sm">
              {t('auth.signup.backToLogin', 'Back to sign in')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const disabled = submitting || waiting || email.trim().length === 0

  return (
    <div className="w-screen h-screen flex justify-center items-center">
      <form className="w-[300px] xs:w-[345px] flex flex-col gap-y-8" onSubmit={onSubmit}>
        <div className="flex flex-col justify-center items-center gap-y-12">
          <UnionLogo className="text-white" />
          <p className="text-xl font-bold text-white">{t('auth.signup.startTitle', 'Create your account')}</p>
        </div>
        <div className="flex flex-col gap-y-2">
          <h5 className="text-white font-semibold text-sm">{t('common.email')}</h5>
          <input
            className="w-full p-4 border rounded-full text-sm text-navyBlue focus:outline-none"
            type="email"
            autoFocus
            inputMode="email"
            autoComplete="email"
            placeholder={t('common.emailPlaceHolder')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setError(undefined)
            }}
          />
          {error && <p className="text-xs text-warn">{error}</p>}
        </div>
        <div className="flex flex-col items-center gap-y-2">
          <button
            type="submit"
            disabled={disabled}
            className={`${disabled ? 'bg-gray-400 text-gray-200' : 'bg-azul text-white hover:bg-primary'} w-60 rounded-full py-3 px-5`}
          >
            {submitting ? t('common.loading', 'Loading…') : waiting ? waitLabel : t('auth.signup.continue', 'Continue')}
          </button>
          <Link to={ROUTE_LOGIN} className="text-white underline hover:text-opacity-70 text-sm">
            {t('auth.signup.haveAccount', 'Already have an account? Sign in')}
          </Link>
        </div>
      </form>
    </div>
  )
}
